<template>
  <ChartBar
    :height="heightSize"
    :chartData="chartData"
    :options="chartOptions"
  />
</template>
<script>
import { mapGetters } from 'vuex'

import { applyColorMode, darkColors } from '@/support'
import ChartBar from '@/components/project/charts/bar/chartConfig'

import { onlyNumbers } from '@/utils/number.utils'
import MathManager from '@/utils/math-manager.utils'

export default {
  components: {
    ChartBar
  },

  props: {
    data: {
      type: Object,
      required: true
    },
    height: {
      type: Number,
      required: false,
      default: 140
    }
  },

  data () {
    return {
      chartOptions: {
        responsive: true,
        tooltips: {
          enabled: true,
          callbacks: {
            label: (tooltipItem, data) => {
              const value = data.datasets[0].data[tooltipItem.index]
              const charges = MathManager.formatNumberDecimal(value)
              return `${charges} ${this.$tc('installments.charges', value)}`
            },
            title: items => {
              const label = this.$t(items[0].label)
              const qtdString = label.split('-')?.[0]

              let qtd = parseInt(qtdString)

              if (Number.isNaN(qtd)) {
                qtd = onlyNumbers(qtdString)
              }

              return `${label} ${this.$tc('installments.title', qtd)}`
            }
          }
        },
        legend: {
          display: false
        },
        scales: {
          xAxes: [
            {
              ticks: {
                callback: label => this.$t(label),
                fontColor: 'rgb(139, 139, 146)'
              },
              display: true,
              gridLines: {
                display: false,
                color: applyColorMode('color')
              }
            }
          ],
          yAxes: [
            {
              display: true,
              gridLines: {
                display: true,
                drawTicks: true,
                borderDash: [8, 4],
                borderDashOffset: 1.8
              },
              ticks: {
                fontColor: 'rgb(139, 139, 146)',
                beginAtZero: true,
                stepSize: this.getStepSize(),
                callback: label => label
              }
            }
          ]
        },
        layout: {
          padding: {
            left: 0,
            right: 20,
            top: 20,
            bottom: 0
          }
        }
      },
      chartData: {
        labels: this.data.label,
        datasets: this.formatObject(this.data)
      }
    }
  },

  computed: {
    ...mapGetters({
      applicationMode: 'GET_THEME'
    }),
    heightSize () {
      if (window.innerWidth < 544) return 150
      else if (window.innerWidth < 768) return 100
      else if (window.innerWidth < 1012) return 150
      else if (window.innerWidth < 1280) return 150
      else return this.height
    }
  },

  watch: {
    data (newValue) {
      this.chartData = Object.assign(
        {},
        {
          labels: newValue.label,
          datasets: this.formatObject(newValue)
        }
      )
    },

    applicationMode () {
      this.updateOptions(applyColorMode('color'), applyColorMode())
      this.chartData = Object.assign(
        {},
        {
          labels: this.data.label,
          datasets: this.formatObject(this.data)
        }
      )
    }
  },

  methods: {
    formatObject (data) {
      return [
        {
          data: data.data,
          backgroundColor: '#30A46C',
          borderColor: darkColors[0],
          maxBarThickness: 40
        }
      ]
    },

    getStepSize () {
      const largest = MathManager.largestNumberInArray(this.data.data)
      const [, exponent] = MathManager.numberBase(largest)
      return Math.pow(10, exponent)
    },

    updateOptions (color, fontColor) {
      this.chartOptions = Object.assign(
        {},
        {
          ...this.chartOptions,
          scales: {
            xAxes: [
              {
                ticks: {
                  fontColor,
                  fontSize: 12
                },
                display: true,
                gridLines: {
                  color
                }
              }
            ],
            yAxes: [
              {
                gridLines: {
                  color
                },
                ticks: {
                  fontColor,
                  beginAtZero: true
                }
              }
            ]
          }
        }
      )
    }
  }
}
</script>
<i18n>
{
  "pt-BR": {
    "installments": {
      "title": "Nenhuma parcela | parcela | parcelas",
      "charges": "Nenhuma cobrança | cobrança | cobranças",
      "one": "1",
      "two-to-three": "2 - 3",
      "four-to-six": "4 - 6",
      "seven-to-nine": "7 - 9",
      "ten-to-twelve": "10 - 12",
      "greater-than-twelve": "Maior do que 12"
    }
  },
  "en-US": {
    "installments": {
      "title": "There aren't installments | installment | installments",
      "charges": "There aren't charges | charge | charges",
      "one": "1",
      "two-to-three": "2 - 3",
      "four-to-six": "4 - 6",
      "seven-to-nine": "7 - 9",
      "ten-to-twelve": "10 - 12",
      "greater-than-twelve": "Greater than 12"
    }
  }
}
</i18n>
